import { useTranslation } from "react-i18next";
import { LogOut } from "lucide-react";
import { Dialog } from "react-aria-components";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";

/**
 * The identity chip and the panel behind it.
 *
 * ## Why a Popover and not a Menu
 * The obvious build is a dropdown menu. It is wrong here: react-aria's `Menu` renders `role="menu"`, which puts
 * screen readers into application mode where they navigate **menuitems only** and skip anything that is not one.
 * The username and roles would therefore be unreachable by exactly the users this panel exists to serve — the
 * design's original "hover to reveal the role" was rejected for being unreachable, so replacing it with something
 * equally unreachable would be no fix at all.
 *
 * So: identity is ordinary text in a Popover body; the interactive actions sit below it. Nothing static is ever
 * placed inside a `role="menu"` container.
 *
 * ## Why the trigger shows a name and never a role
 * `roles` is `string[]` — a user may hold 0, 1 or many — so there is no single "the role" to display. All of them
 * are listed inside. Which role means what is **ADR-004**, which does not exist yet; picking a primary one here
 * would be inventing authorization semantics.
 */
export function AccountPanel({
  displayName,
  initial,
  roles,
  onSignOut,
}: {
  /** Server-resolved and contract-guaranteed non-empty — ~74% of users have no name and fall back to username. */
  displayName: string;
  /** The 1–2 initials (first + last name graphemes), derived from `displayName` so the two can never disagree. */
  initial: string;
  roles: readonly string[];
  /** Omitted until the environment's post-logout URIs are registered — see ADR-005 / hosting-dns.md. */
  onSignOut?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <PopoverTrigger>
      <Button
        variant="ghost"
        // The accessible name carries the PURPOSE, not just the name — "Marek Bartos" alone does not tell a
        // screen-reader user that activating it opens their account panel.
        aria-label={t("shell.accountNamed", { name: displayName })}
        className="gap-2 px-2"
      >
        <Avatar className="size-7">
          {/* No AvatarImage: user photos live in the legacy `file` table and are blocked on the assets plan.
              aria-hidden because the adjacent text already names the person — announcing the initial too would
              read as "M Marek Bartos". */}
          <AvatarFallback aria-hidden="true" className="bg-primary text-primary-foreground text-xs">
            {initial}
          </AvatarFallback>
        </Avatar>
        {/* Hidden below the breakpoint, where the avatar alone stands in — the aria-label keeps it named. */}
        <span className="hidden truncate sm:inline max-w-[12rem]">{displayName}</span>
      </Button>

      <Popover className="w-64 p-0">
        {/*
          `<Dialog>` is kept for its ACCESSIBLE NAME, not for the role or focus management.
          A panel review claimed both were missing without it; that was tested and is not the case — react-aria's
          Popover under DialogTrigger already supplies `role="dialog"` and restores focus to the trigger on close
          (mutation-tested: removing this wrapper keeps both tests green). What it DOES add is the label: an
          unlabelled dialog is announced as just "dialog", which tells a screen-reader user nothing about whose
          account they opened. */}
        <Dialog aria-label={t("shell.accountNamed", { name: displayName })} className="outline-none">
          {/* Ordinary DOM text, deliberately OUTSIDE any role="menu" — see the note above. */}
          <div className="px-3 py-2.5">
            <p className="truncate font-medium">{displayName}</p>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {roles.length > 0 ? roles.join(" · ") : t("shell.noRolesAssigned")}
            </p>
          </div>

          {onSignOut ? (
            <>
              <Separator />
              <div className="p-1">
                <Button variant="ghost" className="w-full justify-start gap-2" onPress={onSignOut}>
                  <LogOut aria-hidden="true" className="size-4" />
                  {t("shell.signOut")}
                </Button>
              </div>
            </>
          ) : null}
        </Dialog>
      </Popover>
    </PopoverTrigger>
  );
}
